import { useQuery } from "@tanstack/react-query";
import { Users, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch, ApiError } from "@/lib/api";

interface TeamMember {
  email: string;
  role: string;
  joined_at?: string;
}

// Read-only team page. Falls back to a friendly "coming soon" card when the
// backend endpoint returns 501 (not implemented).
export function TenantTeam({ tenantCode }: { tenantCode: string }) {
  const { idToken, user } = useAuth();

  const query = useQuery<TeamMember[], ApiError>({
    queryKey: ["team", tenantCode, idToken],
    queryFn: async () => {
      if (!idToken) return [];
      return apiFetch<TeamMember[]>(
        `/api/v1/tenants/${encodeURIComponent(tenantCode)}/users`,
        {},
        idToken,
      );
    },
    enabled: !!idToken,
    retry: false,
  });

  const comingSoon = query.isError && (query.error.status === 501 || query.error.code === "not_implemented");

  return (
    <div className="p-8 space-y-6 max-w-3xl">
      <header className="flex items-center gap-3">
        <div className="rounded-md bg-brand-violet/15 p-2 text-brand-violet">
          <Users className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-4xl">Team</h1>
          <p className="text-sm text-muted-foreground">People with access to this workspace.</p>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <CardDescription>Read-only for now — invitation UI ships next.</CardDescription>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <div className="py-8 flex justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : comingSoon ? (
            <div className="py-8 text-sm text-muted-foreground">Team management coming soon.</div>
          ) : query.isError ? (
            <div className="py-8 text-sm text-signal-failed">Couldn't load team: {query.error.message}</div>
          ) : (
            <ul className="divide-y divide-border">
              {(query.data && query.data.length ? query.data : [{ email: user?.email ?? "you@company.com", role: "owner" }]).map((m) => (
                <li key={m.email} className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm font-medium">{m.email}</p>
                    {m.joined_at && (
                      <p className="text-xs text-muted-foreground mt-0.5">Joined {m.joined_at}</p>
                    )}
                  </div>
                  <span className="font-mono text-xs text-muted-foreground uppercase">{m.role}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
